'use client';

import { useEffect, useRef } from 'react';

import {
  PVC_COCKPIT_SALE,
  type PvcCockpitLanguage,
} from '@/features/sales/pvc-cockpit/pvc-cockpit.data';

interface HotmartCheckoutProps {
  lang?: PvcCockpitLanguage;
}

// Module-level guard: the button renders twice on the page, but the Hotmart
// widget script + CSS must only be injected once.
let hotmartAssetsRequested = false;

function loadHotmartAssets() {
  if (hotmartAssetsRequested) return;
  hotmartAssetsRequested = true;

  const script = document.createElement('script');
  script.src = 'https://static.hotmart.com/checkout/widget.min.js';
  script.async = true;
  document.head.appendChild(script);

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.type = 'text/css';
  link.href = 'https://static.hotmart.com/css/hotmart-fb.min.css';
  document.head.appendChild(link);
}

export function HotmartCheckout({ lang = 'en' }: HotmartCheckoutProps) {
  const anchorRef = useRef<HTMLAnchorElement>(null);

  // Load the Hotmart assets lazily: when the button approaches the viewport,
  // or immediately on hover/focus as a fallback.
  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor || hotmartAssetsRequested) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadHotmartAssets();
          observer.disconnect();
        }
      },
      { rootMargin: '600px' }
    );
    observer.observe(anchor);

    return () => observer.disconnect();
  }, []);

  return (
    <a
      ref={anchorRef}
      href={PVC_COCKPIT_SALE.hotmart.checkoutUrl}
      onPointerEnter={loadHotmartAssets}
      onFocus={loadHotmartAssets}
      className="hotmart-fb hotmart__button-checkout inline-flex! flex-row! items-center! justify-center! gap-2! rounded-lg! bg-green-700! px-8! py-4! text-lg! font-bold! text-white! shadow-lg! transition-all! hover:scale-105! hover:bg-green-800! hover:shadow-xl! active:scale-100! active:bg-green-800!"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shrink-0"
        aria-hidden="true"
      >
        <circle cx="8" cy="21" r="1" />
        <circle cx="19" cy="21" r="1" />
        <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
      </svg>
      <span>{PVC_COCKPIT_SALE.buttonTextByLang[lang]}</span>
    </a>
  );
}
